import { useCallback, useEffect, useRef, useState } from "react";

import { useObjectMemo } from "../../../hooks";
import {
  parseErrorResponse,
  useRequestWithFeedback,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { useAppDispatch } from "../utils";

import { API } from "./api";
import { actions } from "./slice";
import { useSwiftWireTemplates } from "./hooks";

import type { BootstrapFetchRequest } from "../../bootstrap";

export const useFetchTemplates = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchSwiftWireTemplates.Response,
    API.FetchSwiftWireTemplates.Error
  >();
  const [nextPage, setNextPage] = useState<number | null>(null);
  const accumulatedRef = useRef<API.SwiftWireTemplate[]>([]);
  const inFlightRef = useRef(false);

  const dispatch = useAppDispatch();

  const fetchPage = useCallback(
    (page: number) => {
      send({
        action: API.fetchTemplates(page),
        onData: ({ results: templates, next }) => {
          accumulatedRef.current = [...accumulatedRef.current, ...templates];
          if (next) {
            setNextPage(page + 1);
          } else {
            inFlightRef.current = false;
            setNextPage(null);
            dispatch(actions.upsertAll(accumulatedRef.current));
          }
        },
        onError: () => {
          inFlightRef.current = false;
          setNextPage(null);
        },
      });
    },
    [dispatch, send],
  );

  const fetchTemplates = useCallback(() => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    accumulatedRef.current = [];
    fetchPage(1);
  }, [fetchPage]);

  useEffect(() => {
    if (nextPage === null || loading) {
      return;
    }
    fetchPage(nextPage);
  }, [nextPage, loading, fetchPage]);

  const hasData = useSwiftWireTemplates().length > 0;

  return useObjectMemo({
    error: status === "error",
    send: fetchTemplates,
    loading: loading || nextPage !== null,
    hasData,
  }) satisfies BootstrapFetchRequest;
};

export const useCreateOrUpdateTemplateRequest = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateOrUpdateSwiftWireTemplate.Response,
    API.CreateOrUpdateSwiftWireTemplate.Error
  >();

  const dispatch = useAppDispatch();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");
  const createOrUpdateTemplate = useCallback(
    (
      payload: API.CreateOrUpdateSwiftWireTemplate.Request,
      existingSwiftWireTemplateId?: API.SwiftWireTemplateId,
      onFieldError?: (fieldErrors: Record<string, string>) => void,
    ) => {
      return send({
        messaging: {
          toast: {
            success: existingSwiftWireTemplateId
              ? l10n.getString("wire-templates-edit-success")
              : l10n.getString("wire-templates-create-success"),
          },
        },
        action: API.createOrEditTemplate(payload, existingSwiftWireTemplateId),
        onData: (template) => {
          dispatch(actions.updateOne(template));
        },
        onError: async (error) => {
          if (onFieldError) {
            try {
              const json = await error?.response?.json();
              const message = json?.message;
              if (
                message &&
                typeof message === "object" &&
                !Array.isArray(message)
              ) {
                const fieldErrors: Record<string, string> = {};
                Object.entries(message).forEach(([field, msgs]) => {
                  if (Array.isArray(msgs) && typeof msgs[0] === "string") {
                    fieldErrors[field] = msgs[0];
                  } else if (typeof msgs === "string") {
                    fieldErrors[field] = msgs;
                  }
                });
                if (Object.keys(fieldErrors).length > 0) {
                  onFieldError(fieldErrors);
                  return;
                }
              }
            } catch {
              // fall through to generic error handling
            }
          }
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            throwToast({
              kind: "error",
              message: `${errors[0].description}`,
            });
          }
        },
      });
    },
    [dispatch, send, throwToast, l10n],
  );

  return useObjectMemo({
    createOrUpdateTemplate,
    loading,
  });
};

export const useDeleteTemplateRequest = () => {
  const { send, loading } = useRequestWithFeedback();

  const dispatch = useAppDispatch();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");
  const deleteTemplate = useCallback(
    (swiftWireTemplateId: API.SwiftWireTemplateId) => {
      return send({
        messaging: {
          toast: {
            success: l10n.getString("wire-templates-delete-success"),
          },
        },
        action: API.deleteTemplate(swiftWireTemplateId),
        onSuccess: () => {
          dispatch(actions.deleteOne(swiftWireTemplateId));
        },
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            throwToast({
              kind: "error",
              message: `${errors[0].description}`,
            });
          }
        },
      });
    },
    [dispatch, send, throwToast, l10n],
  );

  return useObjectMemo({
    deleteTemplate,
    loading,
  });
};
